import type { Lead, LeadRow } from '../api/types'

/** Equity is the number a loan officer scans a lead list for, and it lives in the
 *  lead platform's `signals` blob rather than in a column of its own.
 *
 *  `usable_equity` is the figure to show: our estimate net of the cushion they
 *  could not borrow against, and the one the lead platform has always displayed.
 *  `equity_est` is the gross number and is deliberately the fallback only — it
 *  overstates what is lendable, so it is used when nothing better exists rather
 *  than preferred. (`attom_equity` is a boolean saying whether the equity figure
 *  came from a recorded loan, not an amount — see crm/ingest.py `_signals`.) */
export function equityOf(lead: Pick<Lead | LeadRow, 'signals'>): number | null {
  const s = lead.signals as Record<string, unknown> | null
  for (const key of ['usable_equity', 'equity_est']) {
    const v = s?.[key]
    if (typeof v === 'number') return v
  }
  return null
}

/** Whether that equity figure came from a recorded loan or from our own estimate.
 *  The detail page says which, because an officer quoting a number on the phone
 *  needs to know how much weight it carries. */
export function equityIsVerified(lead: Pick<Lead | LeadRow, 'signals'>): boolean {
  return (lead.signals as Record<string, unknown> | null)?.attom_equity === true
}
